import React, {useContext, useEffect, useMemo} from "react";
import { useMatch } from "react-router-dom";
import graphql from "babel-plugin-relay/macro";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

import {useLazyLoadQuery, usePaginationFragment} from "react-relay";
import {
	getSingleModulePath,
	MODULES_ID_URL_PARAM,
} from "../../../app/routes/PublishedNodesAndUsersModuleRoutes";
import { UsersWhoMadeContentNodeTable_Query } from "../../../__generated__/UsersWhoMadeContentNodeTable_Query.graphql";
import { Button } from "primereact/button";

import { UserActionColumn } from "./UserActionColumn";
import {
	UsersWhoMadeContentNodeTable_Refetch
} from "../../../__generated__/UsersWhoMadeContentNodeTable_Refetch.graphql";
import {
	UsersWhoMadeContentNodeTable_Fragment$key
} from "../../../__generated__/UsersWhoMadeContentNodeTable_Fragment.graphql";
import {debounce} from "lodash";
import {UserWhoMadeContentNodeSearchContext, UserWhoMadeNodeFilter} from "./UserWhoMadeContentNodeSearchContainer";
import {AccountCodeColumnComponent} from "./account-code-column";
import {UserNameColumn} from "./user-name-column";


const QUERY = graphql`
	query UsersWhoMadeContentNodeTable_Query($contentNodeId: ID, $first: Int, $after: String, $filterByLastName: String, $filterByFirstName: String) {
		...UsersWhoMadeContentNodeTable_Fragment
		@arguments(contentNodeId: $contentNodeId, first: $first,  after: $after, filterByLastName: $filterByLastName, filterByFirstName: $filterByFirstName)
	}`

const FRAGMENT = graphql`
	fragment UsersWhoMadeContentNodeTable_Fragment on Query
	@refetchable(queryName: "UsersWhoMadeContentNodeTable_Refetch")
	@argumentDefinitions(
		first: { type: "Int", defaultValue: 10 }
		after: { type: "String" }
		contentNodeId: {type: "ID"}
		filterByLastName: {type: "String"}
		filterByFirstName: {type: "String"}
	) {
		Admin {
			UsersList  {
				AllUsers (first: $first, after: $after, contentNodeId: $contentNodeId, filterByFirstName: $filterByFirstName, filterByLastName: $filterByLastName)
				@connection(key: "UsersWhoMadeContentNodeTable_AllUsers") {
					pageInfo {
						endCursor
						hasPreviousPage
						hasNextPage
						startCursor
					}
					edges {
						node {
							id
							extensions {
								... on InfectopharmUserExtension {
									firstName
									lastName
									accountCode
								}
							}
							...UserContentSubmissionDialog_UserFragment
							...userNameColumn_Fragment
							...UserActionColumn_NodeFragment
							...accountCodeColumn_Fragment
						}
					}
				}
			}}
	}
`

export const UsersWhoMadeContentNodeTable = () => {
	const match = useMatch(getSingleModulePath(MODULES_ID_URL_PARAM));
	const query = useLazyLoadQuery<UsersWhoMadeContentNodeTable_Query>(
		QUERY,
		{
			contentNodeId: match?.params.moduleId!,
			first: 10,
		},
		{fetchPolicy: "network-only"},
	);
	const {
		data: {
			Admin: {
				UsersList: {
					AllUsers: {edges: users},
				},
			},
		},
		hasPrevious,
		hasNext,
		loadPrevious,
		loadNext,
		refetch,
	} = usePaginationFragment<
		UsersWhoMadeContentNodeTable_Refetch,
		UsersWhoMadeContentNodeTable_Fragment$key
	>(FRAGMENT, query);
	const {filters} = useContext(UserWhoMadeContentNodeSearchContext);
	useEffect(() => {
		debouncedEventHandler(filters);
	}, [filters]);

	const debouncedRefetch = (filters: UserWhoMadeNodeFilter) => {
		refetch(
			{
				filterByLastName: filters.filterByLastName,
				filterByFirstName: filters.filterByFirstName,
				contentNodeId: match?.params.moduleId!,
			},
			{fetchPolicy: "network-only"},
		);
	};

	const debouncedEventHandler = useMemo(
		() => debounce(debouncedRefetch, 1000),
		[filters],
	);

	return (
		<div>

			{users && users.length > 0 ? (
				<DataTable
					className="mb-3"
					value={users?.map((item: any) => item.node)}
				>
					<Column
						header={"Name"}
						body={(item) => <UserNameColumn userFragmentRef={item}/>}
					/>
					<Column header={"Account Code"}
							body={(item: any) =><AccountCodeColumnComponent userFragmentRef={item}/>}
					/>
					<Column
						header={"Aktionen"}
						body={(item) => (
							<UserActionColumn
								userId={item.id}
								contentNodeId={match?.params.moduleId!}
								userFragmentRef={item}
							/>
						)}
					/>
				</DataTable>
			) : (
				<p className="m-2">Keine Benutzer haben dieses Modul gestartet</p>
			)}
			<div className="flex justify-content-center align-items-center">
				<Button
					disabled={!hasPrevious}
					onClick={() => loadPrevious(20)}
					className="mr-3 p-button-secondary"
				>
					Zurück
				</Button>
				<Button
					className="p-button-secondary"
					disabled={!hasNext}
					onClick={() => loadNext(20)}
				>
					Weiter
				</Button>
			</div>
		</div>
	);
};
